/**
 * Fallout 1's half of the constant tables, and the accessor that picks a game.
 *
 * Same rule as `constants.ts`: nothing here is inferred, everything cites the
 * fallout1-ce file it was read from (`~/f2ce-spike/fallout1-ce/src/game/...`).
 *
 * Most of Fallout 1's tables are the *prefix* of Fallout 2's — the two games
 * share the `Stat`, `Skill`, `KillType` and `Perk` enums up to the point where
 * Fallout 2 adds to them, in that order, with no renumbering. That is a fact
 * worth checking rather than assuming, and it was: `stat_defs.h` (35 saveable
 * stats, same order), `skill_defs.h` (18, same order), `proto_types.h:102-117`
 * (`KILL_TYPE_MAN` .. `KILL_TYPE_PLANT`, which is Fallout 2's first fifteen),
 * `perk_defs.h:6-71` (`PERK_AWARENESS` .. `PERK_COMBAT_ARMOR`, Fallout 2's first
 * sixty-four). So those are derived by slicing rather than retyped, which makes
 * a future divergence a compile error instead of a silent disagreement.
 *
 * Traits are the exception and the reason this file cannot just be a set of
 * slices: `Trait` (trait.h:12-28) has `TRAIT_NIGHT_PERSON` at index 13 where
 * Fallout 2 has `TRAIT_SEX_APPEAL`. Same count, different meaning, same byte.
 */

import {
  KILL_TYPE_NAMES,
  PERK_NAMES,
  SKILL_NAMES,
  STAT_NAMES,
  type SkillDescription,
} from './constants';
import * as FO2 from './constants';
import { PERK_ROWS, SKILL_FRM_IDS, TRAIT_FRM_IDS, type PerkRow } from './perktable';
import { PERK_ROWS_FO1, SKILL_FRM_IDS_FO1, TRAIT_FRM_IDS_FO1 } from './perktable-fo1';
import { formatFor, type SaveGameType } from './gamedefs';

/** fallout1-ce perk_defs.h:71 — `PERK_COUNT`. */
export const PERK_COUNT_FO1 = 64;

/** fallout1-ce proto_types.h:117 — `KILL_TYPE_COUNT`. */
export const KILL_TYPE_COUNT_FO1 = 15;

/** fallout1-ce trait.h:28 — `TRAIT_COUNT`, and `PC_TRAIT_MAX` at trait.h:9. */
export const TRAIT_COUNT_FO1 = 16;

/** fallout1-ce stat_defs.h:18 — `PC_LEVEL_MAX`. Fallout 2's is 99. */
export const PC_LEVEL_MAX_FO1 = 21;

/** fallout1-ce skill.cc:31 — `SKILL_LEVEL_MAX`. Fallout 2's `skillMaximum` is 300. */
export const SKILL_MAXIMUM_FO1 = 200;

/**
 * fallout1-ce trait.h:12 — the `Trait` enum. Identical to Fallout 2's except
 * index 13, which is Night Person rather than Sex Appeal.
 */
export const TRAIT_NAMES_FO1 = [
  'FAST_METABOLISM', 'BRUISER', 'SMALL_FRAME', 'ONE_HANDER', 'FINESSE', 'KAMIKAZE',
  'HEAVY_HANDED', 'FAST_SHOT', 'BLOODY_MESS', 'JINXED', 'GOOD_NATURED', 'CHEM_RELIANT',
  'CHEM_RESISTANT', 'NIGHT_PERSON', 'SKILLED', 'GIFTED',
] as const;

/** fallout1-ce proto_types.h:102 — `KillType`, which is Fallout 2's first fifteen. */
export const KILL_TYPE_NAMES_FO1: readonly string[] = KILL_TYPE_NAMES.slice(0, KILL_TYPE_COUNT_FO1);

/** fallout1-ce perk_defs.h:6 — `Perk`, which is Fallout 2's first sixty-four. */
export const PERK_NAMES_FO1: readonly string[] = PERK_NAMES.slice(0, PERK_COUNT_FO1);

/**
 * fallout1-ce stat.cc `stat_data` — `[minimumValue, maximumValue]` per stat.
 *
 * Fallout 1 leaves most derived stats uncapped (`INT_MAX`) where Fallout 2 gave
 * them tight ceilings, and its Age tops out at 35 rather than 101. The Better
 * Criticals floor of -60 carries fallout1-ce's own "on par with Fallout 2" fix.
 */
const INT_MAX = 0x7fffffff;
export const STAT_BOUNDS_FO1: ReadonlyArray<readonly [number, number]> = [
  [1, 10], [1, 10], [1, 10], [1, 10], [1, 10], [1, 10], [1, 10],
  [0, INT_MAX], // MAXIMUM_HIT_POINTS
  [0, INT_MAX], // MAXIMUM_ACTION_POINTS
  [0, INT_MAX], // ARMOR_CLASS
  [0, INT_MAX], // UNARMED_DAMAGE
  [0, INT_MAX], // MELEE_DAMAGE
  [0, INT_MAX], // CARRY_WEIGHT
  [0, INT_MAX], // SEQUENCE
  [0, INT_MAX], // HEALING_RATE
  [0, 100], // CRITICAL_CHANCE
  [-60, 100], // BETTER_CRITICALS
  [0, 100], [0, 100], [0, 100], [0, 100], [0, 100], [0, 100], [0, 100],
  [0, 90], [0, 90], [0, 90], [0, 90], [0, 90], [0, 100], [0, 90],
  [0, 100], // RADIATION_RESISTANCE
  [0, 100], // POISON_RESISTANCE
  [16, 35], // AGE
  [0, 1], // GENDER
  [0, 2000], [0, 2000], [0, 2000],
];

/**
 * fallout1-ce skill.cc `skill_data` — the columns `skill_level` reads, in
 * `SKILL_NAMES` order.
 *
 * Fallout 1's numbers are genuinely different from Fallout 2's, not a
 * re-encoding of them: Small Guns starts at 35 with a ×1 Agility term where
 * Fallout 2 starts at 5 with a ×4 one. And the two-stat skills *average*
 * (`(stat_level(stat1) + stat_level(stat2)) * modifier / 2`, skill.cc:186) where
 * Fallout 2 adds each stat's contribution separately. `averagesStats` carries
 * that difference so one formula can serve both engines.
 */
export const SKILL_DESCRIPTIONS_FO1: ReadonlyArray<SkillDescription> = [
  { defaultValue: 35, statModifier: 1, stat1: 5, stat2: -1, baseValueMult: 1 }, // SMALL_GUNS
  { defaultValue: 10, statModifier: 1, stat1: 5, stat2: -1, baseValueMult: 1 }, // BIG_GUNS
  { defaultValue: 10, statModifier: 1, stat1: 5, stat2: -1, baseValueMult: 1 }, // ENERGY_WEAPONS
  { defaultValue: 65, statModifier: 1, stat1: 5, stat2: 0, baseValueMult: 1 }, // UNARMED
  { defaultValue: 55, statModifier: 1, stat1: 5, stat2: 0, baseValueMult: 1 }, // MELEE_WEAPONS
  { defaultValue: 40, statModifier: 1, stat1: 5, stat2: -1, baseValueMult: 1 }, // THROWING
  { defaultValue: 30, statModifier: 1, stat1: 1, stat2: 4, baseValueMult: 1 }, // FIRST_AID
  { defaultValue: 15, statModifier: 1, stat1: 1, stat2: 4, baseValueMult: 1 }, // DOCTOR
  { defaultValue: 25, statModifier: 1, stat1: 5, stat2: -1, baseValueMult: 1 }, // SNEAK
  { defaultValue: 20, statModifier: 1, stat1: 1, stat2: 5, baseValueMult: 1 }, // LOCKPICK
  { defaultValue: 20, statModifier: 1, stat1: 5, stat2: -1, baseValueMult: 1 }, // STEAL
  { defaultValue: 20, statModifier: 1, stat1: 1, stat2: 5, baseValueMult: 1 }, // TRAPS
  { defaultValue: 25, statModifier: 2, stat1: 4, stat2: -1, baseValueMult: 1 }, // SCIENCE
  { defaultValue: 20, statModifier: 1, stat1: 4, stat2: -1, baseValueMult: 1 }, // REPAIR
  { defaultValue: 25, statModifier: 2, stat1: 3, stat2: -1, baseValueMult: 1 }, // SPEECH
  { defaultValue: 20, statModifier: 2, stat1: 3, stat2: -1, baseValueMult: 1 }, // BARTER
  { defaultValue: 20, statModifier: 3, stat1: 6, stat2: -1, baseValueMult: 1 }, // GAMBLING
  { defaultValue: 5, statModifier: 1, stat1: 2, stat2: 4, baseValueMult: 1 }, // OUTDOORSMAN
];

/** Index of a stat/skill/perk/trait by name, or -1. */
function indexer(names: readonly string[]): (name: string) => number {
  const map = new Map(names.map((n, i) => [n, i]));
  return (name) => map.get(name.toUpperCase()) ?? -1;
}

/**
 * Everything the editor needs that differs between the two games, in one
 * bundle, so a caller with a `SaveDat` in hand can say `forGame(save.game)` and
 * stop caring which engine wrote it.
 *
 * The Fallout 2 bundle is the existing module's exports verbatim — no values are
 * re-derived — so `forGame('fo2')` cannot drift from what the codec did before
 * Fallout 1 existed here.
 */
export type GameConstants = {
  game: SaveGameType;
  statNames: readonly string[];
  statBounds: ReadonlyArray<readonly [number, number]>;
  skillNames: readonly string[];
  skillDescriptions: ReadonlyArray<SkillDescription>;
  /** True when a two-stat skill averages its stats rather than summing them. */
  skillAveragesStats: boolean;
  /** `skillMaximum` / `SKILL_LEVEL_MAX` — the value clamp at the end of the formula. */
  skillMaximum: number;
  traitNames: readonly string[];
  traitCount: number;
  perkNames: readonly string[];
  perkCount: number;
  perkRows: readonly PerkRow[];
  killTypeNames: readonly string[];
  killTypeCount: number;
  pcLevelMax: number;
  /** Skilldex art ids, for the picker cards. */
  traitFrmIds: readonly number[];
  skillFrmIds: readonly number[];
  statIndex: (name: string) => number;
  skillIndex: (name: string) => number;
  perkIndex: (name: string) => number;
  traitIndex: (name: string) => number;
  killTypeIndex: (name: string) => number;
};

const FO2_CONSTANTS: GameConstants = {
  game: 'fo2',
  statNames: STAT_NAMES,
  statBounds: FO2.STAT_BOUNDS,
  skillNames: SKILL_NAMES,
  skillDescriptions: FO2.SKILL_DESCRIPTIONS,
  skillAveragesStats: false,
  skillMaximum: FO2.SKILL_MAXIMUM,
  traitNames: FO2.TRAIT_NAMES,
  traitCount: FO2.TRAIT_COUNT,
  perkNames: PERK_NAMES,
  perkCount: FO2.PERK_COUNT,
  perkRows: PERK_ROWS,
  killTypeNames: KILL_TYPE_NAMES,
  killTypeCount: FO2.KILL_TYPE_COUNT,
  pcLevelMax: FO2.PC_LEVEL_MAX,
  traitFrmIds: TRAIT_FRM_IDS,
  skillFrmIds: SKILL_FRM_IDS,
  statIndex: FO2.statIndex,
  skillIndex: FO2.skillIndex,
  perkIndex: FO2.perkIndex,
  traitIndex: FO2.traitIndex,
  killTypeIndex: FO2.killTypeIndex,
};

const FO1_CONSTANTS: GameConstants = {
  game: 'fo1',
  statNames: STAT_NAMES,
  statBounds: STAT_BOUNDS_FO1,
  skillNames: SKILL_NAMES,
  skillDescriptions: SKILL_DESCRIPTIONS_FO1,
  skillAveragesStats: true,
  skillMaximum: SKILL_MAXIMUM_FO1,
  traitNames: TRAIT_NAMES_FO1,
  traitCount: TRAIT_COUNT_FO1,
  perkNames: PERK_NAMES_FO1,
  perkCount: PERK_COUNT_FO1,
  perkRows: PERK_ROWS_FO1,
  killTypeNames: KILL_TYPE_NAMES_FO1,
  killTypeCount: KILL_TYPE_COUNT_FO1,
  pcLevelMax: PC_LEVEL_MAX_FO1,
  traitFrmIds: TRAIT_FRM_IDS_FO1,
  skillFrmIds: SKILL_FRM_IDS_FO1,
  statIndex: FO2.statIndex,
  skillIndex: FO2.skillIndex,
  perkIndex: indexer(PERK_NAMES_FO1),
  traitIndex: indexer(TRAIT_NAMES_FO1),
  killTypeIndex: indexer(KILL_TYPE_NAMES_FO1),
};

/** The constant bundle for a game. Cheap: both bundles are built once. */
export function forGame(game: SaveGameType): GameConstants {
  return game === 'fo1' ? FO1_CONSTANTS : FO2_CONSTANTS;
}

/** Convenience for callers that hold a `SaveDat`-shaped thing. */
export const constantsFor = (save: { game: SaveGameType }): GameConstants => forGame(save.game);

/** Re-exported so callers need one import for "what game is this". */
export { formatFor };
